<template>
  <div>
    <loading-panel :loading="loading"></loading-panel>

    <card>
      <!-- Header: active/archived pills + create -->
      <div class="row mb-3">
        <div class="col">
          <div
            class="btn-group btn-group-toggle"
            :class="'float-left'"
            data-toggle="buttons"
          >
            <label
              v-for="(option, index) in categories"
              :key="index + j"
              class="btn btn-sm btn-primary btn-simple"
              :class="{ active: option.active }"
              :id="String(index)"
            >
              <input
                type="radio"
                @click="switchTab(index)"
                name="options"
                autocomplete="off"
                :checked="option.active"
              />
              <span style="font-size: medium; font-weight: lighter" class="d-none d-sm-block">{{ option.name }}</span>
              <span class="d-block d-sm-none">
                <i :class="option.icon"></i>
              </span>
            </label>
          </div>
        </div>
        <div class="col text-right d-sm-block d-none">
          <base-button @click="createPassType" type="primary" size="sm">
            <i class="tim-icons mr-1 el-icon-plus"></i> Create pass type
          </base-button>
        </div>
        <div class="col text-right d-sm-none d-block">
          <base-button @click="createPassType" type="primary" size="sm">
            <i class="tim-icons el-icon-plus"></i>
          </base-button>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="visiblePassTypes.length === 0 && !loading" class="pl-empty-state mt-4">
        <img src="/img/pandas/empty-transactions.png" alt="No pass types" class="empty-state-img" />
        <h4 class="empty-state-text" v-if="view === 'active'">No pass types created yet</h4>
        <h4 class="empty-state-text" v-else>No archived pass types</h4>
        <p class="pl-empty-state__hint" v-if="view === 'active'">
          Passes automatically cover a set number of tickets when the pass holder books, for a set time window. Grant them to customers from their profile, or sell them online
        </p>
      </div>

      <!-- Card list -->
      <div v-if="visiblePassTypes.length > 0" class="pl-list mt-3">
        <div
          v-for="passType in visiblePassTypes"
          :key="'PT' + passType.id"
          class="pl-card"
          :class="{ 'pl-card--archived': passType.is_archived === 1 }"
          @click="editPassType(passType)"
        >
          <div class="pl-card__body">
            <div class="pl-card__info">
              <div class="pl-card__label text-white">{{ passType.name }}</div>

              <div class="pl-card__meta">
                <span class="pl-card__meta-detail">{{ durationText(passType) }}</span>
                <span class="pl-card__meta-detail">{{ allowanceText(passType) }}</span>
                <span class="pl-card__meta-detail">{{ gamesText(passType) }}</span>
              </div>

              <div class="pl-card__usage">
                <span class="pl-card__usage-label">Active passes</span>
                <span class="pl-card__usage-text">{{ passType.active_passes }}</span>
                <template v-if="passType.is_purchasable === 1">
                  <span class="pl-card__usage-label">Online price</span>
                  <span class="pl-card__usage-text">{{ priceText(passType) }}</span>
                </template>
                <template v-if="passType.internal_label">
                  <span class="pl-card__usage-label">Label</span>
                  <span class="pl-card__usage-text">{{ passType.internal_label }}</span>
                </template>
              </div>
            </div>

            <!-- Right: badges + actions (click-stopped so the row click cannot swallow the menu) -->
            <div class="pl-card__actions" @click.stop>
              <span v-if="passType.is_purchasable === 1 && passType.is_archived === 0" class="badge-tag badge-tag--primary">Sold online</span>
              <span v-if="passType.is_archived === 1" class="badge-tag badge-tag--archived">Archived</span>

              <el-dropdown @click.native.stop>
                <base-button simple type="primary" link size="sm">
                  <i class="tim-icons el-icon-more"></i>
                </base-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <a class="dropdown-item mt-1 mb-2" href="#" @click.prevent="editPassType(passType)">
                      <i class="tim-icons icon-pencil mr-2"></i> Edit pass type
                    </a>
                    <a v-if="passType.is_archived === 0" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="archivePrompt(passType)">
                      <i class="tim-icons icon-app mr-2"></i> Archive
                    </a>
                    <a v-if="passType.is_archived === 1" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="setArchived(passType, false)">
                      <i class="tim-icons icon-refresh-02 mr-2"></i> Restore
                    </a>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>
        </div>
      </div>
    </card>

    <pass-type-drawer
      :show="drawerOpen"
      :pass-type="selectedPassType"
      @close="drawerOpen = false"
      @saved="getPassTypes(false)"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import axios from 'axios'
import swal from 'sweetalert2'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatCurrency, getGroupSettings } from '@/util/currencyFormat'
import PassTypeDrawer from './PassTypeDrawer.vue'

const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}`

const loading = ref(true)
const passTypes = ref<any[]>([])
const view = ref('active')
const j = ref(0)
const categories = ref([
  { name: 'Active', icon: 'tim-icons icon-check-2', active: true },
  { name: 'Archived', icon: 'tim-icons icon-trash-simple', active: false }
])

const drawerOpen = ref(false)
const selectedPassType = ref<any>(null)

const visiblePassTypes = computed(() =>
  passTypes.value.filter((pt) => (view.value === 'archived' ? pt.is_archived === 1 : pt.is_archived === 0)))

function durationText(passType: any) {
  const plural = passType.duration_value === 1 ? '' : 's'
  return `${passType.duration_value} ${passType.duration_unit}${plural}`
}

function allowanceText(passType: any) {
  const tickets = `${passType.ticket_allowance} ticket${passType.ticket_allowance === 1 ? '' : 's'}`
  return passType.allowance_type === 'per_booking' ? `${tickets} per booking` : `${tickets} total`
}

function gamesText(passType: any) {
  if (passType.applies_to_all_games === 1) return 'All events'
  const count = (passType.games || []).length
  return `${count} event${count === 1 ? '' : 's'}`
}

function priceText(passType: any) {
  const group = getGroupSettings()
  return formatCurrency(passType.price, {
    currency: group.currency,
    currencyFormat: group.currency_format,
    numberFormat: group.number_format
  })
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

async function getPassTypes(showLoader = true) {
  if (showLoader) loading.value = true
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/pass-types`, config)
    passTypes.value = response.data.pass_types || []
  } catch (err: any) {
    if (err?.response?.status === 401 || err?.response?.status === 403) {
      proxy.$router.push('/forbidden')
      return
    }
    swal('Error!', 'Failed to load pass types', 'error')
  } finally {
    loading.value = false
  }
}

function switchTab(index: number) {
  categories.value.forEach((option, i) => { option.active = i === index })
  j.value += 1
  view.value = index === 1 ? 'archived' : 'active'
}

function createPassType() {
  selectedPassType.value = null
  drawerOpen.value = true
}

function editPassType(passType: any) {
  selectedPassType.value = passType
  drawerOpen.value = true
}

function archivePrompt(passType: any) {
  swal({
    title: 'Archive this pass type?',
    text: 'Existing customer passes keep working, but no new passes of this type can be granted or sold.',
    type: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Archive',
    cancelButtonText: 'Keep it'
  }).then((result: any) => {
    if (!result.value) return
    setArchived(passType, true)
  })
}

async function setArchived(passType: any, archived: boolean) {
  try {
    const config = await authConfig()
    await axios.put(`${baseUrl}/pass-types/${passType.id}/archive`, { is_archived: archived ? 1 : 0 }, config)
    proxy.$notify({
      type: 'success',
      message: archived ? 'Pass type archived' : 'Pass type restored',
      icon: 'tim-icons icon-check-2'
    })
  } catch (err) {
    swal('Error!', archived ? 'The pass type could not be archived' : 'The pass type could not be restored', 'error')
  } finally {
    getPassTypes(false)
  }
}

onMounted(() => {
  getPassTypes()
})
</script>

<script lang="ts">
// el-dropdown is not globally registered (see globalComponents.js); register
// locally like every other consumer. Unaliased imports only.
import { Dropdown, DropdownMenu } from 'element-ui'

export default {
  name: 'passes-list',
  components: {
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu
  }
}
</script>

<style scoped>
/* Row-card list, same language as the campaigns list (cl-card) and
   PaymentPagesList (pp-card). Layout + theme-neutral tints only. */
.pl-empty-state {
  text-align: center;
  padding: 2.5rem 1rem;
}
.pl-empty-state__hint {
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  max-width: 480px;
  margin: 8px auto 0;
}

.pl-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.pl-card {
  background: rgba(128, 128, 128, 0.05);
  border-left: 3px solid #e14eca;
  border-radius: 0.375rem;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s ease;
}
.pl-card:hover {
  background: rgba(128, 128, 128, 0.12);
}
.pl-card--archived {
  opacity: 0.5;
  border-left-color: rgba(125, 125, 125, 0.4);
}
.pl-card__body {
  display: flex;
  align-items: center;
  gap: 14px;
}

.pl-card__info {
  flex: 1;
  min-width: 0;
}
.pl-card__label {
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pl-card__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 3px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);
}
.pl-card__meta-detail {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pl-card__meta-detail:not(:first-child)::before {
  content: '·';
  margin-right: 10px;
  color: rgba(125, 125, 125, 0.6);
}

.pl-card__usage {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.55);
}
.pl-card__usage-label {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
}
.pl-card__usage-label:not(:first-child) {
  margin-left: 8px;
}
.pl-card__usage-text {
  color: rgba(255, 255, 255, 0.75);
}

.pl-card__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

@media (max-width: 575px) {
  .pl-card__body {
    gap: 10px;
  }
  .pl-card__usage-label {
    display: none;
  }
}
</style>

<style>
.white-content .pl-empty-state__hint {
  color: rgba(0, 0, 0, 0.6);
}
.white-content .pl-card {
  background: rgba(0, 0, 0, 0.02);
}
.white-content .pl-card:hover {
  background: rgba(0, 0, 0, 0.05);
}
.white-content .pl-card__meta {
  color: rgba(0, 0, 0, 0.7);
}
.white-content .pl-card__usage {
  color: rgba(0, 0, 0, 0.5);
}
.white-content .pl-card__usage-label {
  color: rgba(0, 0, 0, 0.5);
}
.white-content .pl-card__usage-text {
  color: rgba(0, 0, 0, 0.7);
}
</style>
